<template>
  <VcWidget class="product-info px-4 md:px-0" size="lg">
    <div class="product-info__container">
      <div class="flex lg:hidden flex-col w-full mb-4">
        <VcTypography tag="h1" variant="h3" class="text-[--color-body-text]">
          {{ productName }}
        </VcTypography>
        <div class="clear-both">
          <span v-if="productGroupMaterial">{{ productGroupMaterial }}</span>
          <span v-if="productGroupMaterial && productFinish" class="mx-1">|</span>
          <span v-if="productFinish">{{ productFinish }}</span>
        </div>
      </div>

      <div class="product-info__side">
        <ImageGallery :images="product.images" :fallbackSrc="NO_PRODUCT_IMAGE_URL">
          <template #badges>
            <BadgesWrapper>
              <PurchasedBeforeBadge v-if="product.isPurchased" />

              <DiscountBadge static :price="product.price" />
            </BadgesWrapper>
          </template>
        </ImageGallery>

        <ProductVideos class="product-info__videos" :videos="product.videos" />
      </div>

      <div class="product-info__print">
        <VcImage :src="product.imgSrc" :fallbackSrc="NO_PRODUCT_IMAGE_URL" class="product-info__print-img" />
      </div>

      <div class="product-info__content flex flex-col">
        <div class="hidden lg:flex flex-col w-full">
          <VcTypography tag="h1" variant="h3" class="text-[--color-body-text]">
            {{ productName }}
          </VcTypography>
          <div class="clear-both">
            <span v-if="productGroupMaterial">{{ productGroupMaterial }}</span>
            <span v-if="productGroupMaterial && productFinish" class="mx-1">|</span>
            <span v-if="productFinish">{{ productFinish }}</span>
          </div>
        </div>

        <div v-if="isVariant" class="mt-2 flex flex-wrap gap-5">
          <VcCopyText :text="product.code" :notification="$t('pages.product.sku_copied_message')">
            <span class="text-base text-secondary-900">
              {{ $t("pages.product.sku_label") }}
              <span class="font-black">#{{ product.code }}</span>
            </span>
          </VcCopyText>
        </div>

        <div v-if="model?.blocks?.length" class="product-info__blocks">
          <template v-for="(block, index) in model.blocks">
            <component
              :is="block.type"
              v-if="block.type !== 'product-variations' && block.type !== 'product-description'"
              :key="block.id || index"
              :model="block"
              :product="product"
              :featured="true"
            />
            <div v-if="block.type === 'product-properties'">
              <a href="#all-properties" class="text-sm mr-[2rem] text-[--color-link] font-semibold">{{
                $t("shared.catalog.product_details.properties_featured_show_all")
              }}</a>
              <!-- BB2B-193 Show variants button on variant page to return to master -->
              <!-- Innovadis custom: sanitize productGroup URL before binding to href -->
              <a
                v-if="productGroup && isVariant"
                :href="getSafeUrl(productGroup)"
                class="text-sm text-[--color-link] font-semibold"
                >{{ $t("shared.catalog.product_details.show_variants") }}</a
              >
            </div>
          </template>
        </div>

        <div v-if="product.isConfigurable" class="product-info__config">
          <VcButton
            color="secondary"
            variant="outline"
            prepend-icon="cube-transparent"
            @click="handleCreateConfiguration"
            size="lg"
          >
            {{ $t("shared.catalog.product_details.create_configuration_button") }}
          </VcButton>
        </div>

        <VcProductActions v-if="isVariant" :direction="'horizontal'">
          <AddToList :product="product" />
          <AddToCompareCatalog v-if="$cfg.product_compare_enabled" :product="product" />
        </VcProductActions>

        <div
          v-if="showPriceInfo"
          class="flex flex-row w-full mt-8"
          :class="{ 'gap-6': isVariant || (priceUnitText && priceUnitText !== 'N/A') }"
        >
          <div v-if="!isVariant">
            <!--  BB2B-122 Hide price on product tile for now due to performance issues on min variation price
            <InnoProductPrice
              v-if="priceUnitText && priceUnitText !== 'N/A'"
              :actual-price="actualPriceWithVariations"
              :list-price="listPriceWithVariations"
              :with-from-label="withFromLabel"
              :price-unit-text="priceUnitText"
              :single-line="false"
              class="pr-2"
            /> -->
          </div>
          <div
            v-else
            class="flex flex-col min-w-[100px] product-info__price align-center"
            :class="{ '-mt-6': withFromLabel || showStripedPrice }"
          >
            <InnoProductPrice
              class="min-h-9 text-4xl text-right mb-2"
              :actual-price="price?.actual"
              :list-price="price?.list"
              :with-from-label="withFromLabel"
              :price-unit-text="priceUnitText"
              :sales-unit-text="getProductSalesUnitText(product)"
            />
          </div>
          <div v-if="!isVariant && product.hasVariations" class="product-info__variations_link w-full">
            <a
              href="#all-variations"
              class="vc-button group vc-button--size--lg vc-button--color--primary vc-button--solid--primary vc-button--shape--pill w-full h-[48px] py-[10px]"
              >{{ $t("shared.catalog.product_details.show_variants") }}</a
            >
          </div>
          <div v-else-if="isVariant" class="flex flex-col grow">
            <AddToCart
              :product="product"
              :quantity-operators="$cfg.quantity_operators_enabled"
              :show-empty-details="false"
              :wide="true"
              class="grid"
            >
              <InStock
                v-if="isAuthenticated"
                :is-in-stock="product.availabilityData.isInStock"
                :quantity="product.availabilityData.availableQuantity"
                class="mt-1"
              />

              <CountInCart :product-id="product.id" class="mt-1" />
            </AddToCart>
          </div>
        </div>
      </div>
    </div>
  </VcWidget>
</template>

<script setup lang="ts">
import { getSafeUrl } from "@/core/utilities/url/safeUrl";
import { computed } from "vue";
import { ImageGallery, DiscountBadge, ProductVideos } from "@/shared/catalog";
import {
  CUSTOM_PROPERTY_GROUP_MATERIAL,
  CUSTOM_PROPERTY_FINISH,
  CUSTOM_PROPERTY_ARTICLE_GROUP,
} from "@/shared/common/constants";
import {
  getCustomPropertyValue,
  getProductName,
  getProductPriceUnitText,
  getProductSalesUnitText,
} from "@/core/utilities/innovadis";
import { AddToCart } from "@/shared/cart";
import { AddToCompareCatalog } from "@/shared/compare";
import { AddToList } from "@/shared/wishlists";
import { shouldUseActualPrice } from "@/ui-kit/utilities/price";
import { useUser } from "@/shared/account";
import { NO_PRODUCT_IMAGE_URL } from "@/core/constants"; // Innovadis custom
import InnoProductPrice from "@/ui-kit//components/molecules/innovadis/inno-product-price.vue";
import CountInCart from "@/shared/catalog/components/count-in-cart.vue";
import InStock from "@/shared/catalog/components/in-stock.vue";
import BadgesWrapper from "@/shared/catalog/components/badges-wrapper.vue";
import PurchasedBeforeBadge from "@/shared/catalog/components/purchased-before-badge.vue";
import type { IPageContent } from "../../types";
import type { Product } from "@/core/api/graphql/types";

interface IProps {
  product: Product;
  model: IPageContent;
}

const props = defineProps<IProps>();

const { isAuthenticated } = useUser();

const showPriceInfo = computed(() => props.model?.showPriceInfo ?? true);

// Innovadis custom: get product name
const productName = computed(() => getProductName(props.product, true));

// Innovadis custom: get product group value from product properties
const productGroup = computed(() =>
  (getCustomPropertyValue(props.product.properties, CUSTOM_PROPERTY_ARTICLE_GROUP) as string)?.toLowerCase(),
);

// Innovadis custom: get group material value from product properties
const productGroupMaterial = computed(() =>
  getCustomPropertyValue(props.product.properties, CUSTOM_PROPERTY_GROUP_MATERIAL),
);

// Innovadis custom: get finish value from product properties
const productFinish = computed(() => getCustomPropertyValue(props.product.properties, CUSTOM_PROPERTY_FINISH));

// Innovadis custom: make sure if a product with no variations is a variant
// masters with no visible variations appeared, which are mistakenly handled as variations when just checking for hasVariations value
// products having id starting with p are masters, starting with v are variations
const isVariant = computed(() => !props.product?.hasVariations && !props.product?.id?.startsWith("p"));

//const actualPriceWithVariations = computed(() => props.product.minVariationPrice?.actual ?? props.product.price.actual);
//const listPriceWithVariations = computed(() => props.product.minVariationPrice?.list ?? props.product.price.list);
const withFromLabel = computed(() => props.product.hasVariations || props.product.isConfigurable);
const showStripedPrice = computed(
  () =>
    !props.product.hasVariations &&
    !props.product.isConfigurable &&
    shouldUseActualPrice(props.product.price?.list, props.product.price?.actual) &&
    props.product.price?.list?.amount &&
    props.product.price.list.amount > 0,
);
//const price = computed(() => (props.product.hasVariations ? props.product.minVariationPrice : props.product.price));
const price = computed(() => (props.product.hasVariations ? null : props.product.price));

// Innovadis custom: get price unit text
const priceUnitText = computed(() => getProductPriceUnitText(props.product));

function handleCreateConfiguration() {
  const productConfigurationElement = document.getElementById("product-configuration-anchor");
  if (productConfigurationElement) {
    productConfigurationElement.scrollIntoView({ block: "center", inline: "nearest", behavior: "smooth" });
  }
}
</script>

<style lang="scss">
.product-info {
  @apply border-none shadow-none;

  &__container {
    @apply flex flex-col lg:flex-row lg:gap-8 print:flex-row print:gap-4;
  }

  &__side {
    @apply flex-none lg:w-80 xl:w-[30rem] 2xl:w-[36rem] print:hidden;
  }

  &__videos {
    @apply mt-8 lg:mt-3;
  }

  &__print {
    @apply hidden aspect-square w-40 flex-none print:block;
  }

  &__print-img {
    @apply w-full rounded border;
  }

  &__content {
    @apply mt-5 lg:mt-0 lg:grow print:mt-5 print:grow;

    .vc-product-price__price-unit {
      @apply text-base font-normal;
    }

    .vc-product-price__sales-unit {
      @apply text-xxs font-normal text-[--color-neutral-500] leading-[1.25rem];
    }
  }

  &__blocks {
    @apply flex flex-col gap-6 mb-6;
  }

  .vc-widget__slot {
    @apply p-0;
  }

  .image-gallery__images-container {
    @apply border-[--color-gray-line];
  }

  .inno-add-to-cart {
    .inno-input__container {
      .inno-input__decorator:last-of-type {
        @apply w-full;
      }
    }
  }

  .price-on-request {
    @apply text-base;
  }
}
</style>
